import { useContext, useRef, useEffect, useState, useCallback } from 'react'
import { Form, Select, Spin } from 'antd'
import { useQuery } from 'react-query'
import debounce from 'lodash.debounce'

import { AppContext } from '../context/AppContext'
import { useTranslation } from 'react-i18next'

const { Option } = Select

const LoadingSelect = ({
    onChange,
    request,
    speKey,
    label,
    isSearch,
    remoteSearch,
    autoFocus,
    noAll,
    defaultValue,
    value,
    otherParams = {},
    multiple = false,
    optionProcessor,
}) => {
    const { token } = useContext(AppContext)
    const { t } = useTranslation()
    const inputRef = useRef(null)
    const [searchText, setSearchText] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')

    const debouncedSetSearch = useCallback(
        debounce((text) => setDebouncedSearch(text), 1000),
        []
    )

    useEffect(() => {
        debouncedSetSearch(searchText)
    }, [searchText, debouncedSetSearch])

    const includeIds =
        remoteSearch && value ? (Array.isArray(value) ? value : [value]) : []

    const options = useQuery(
        [
            `get${speKey.charAt(0).toUpperCase() + speKey.slice(1)}Select`,
            {
                token: token.token,
                filter: { ...otherParams },
                ...(remoteSearch
                    ? { search: debouncedSearch, includeIds }
                    : {}),
            },
        ],
        request
    )

    useEffect(() => {
        if (inputRef?.current) {
            if (autoFocus) {
                inputRef.current.focus({
                    cursor: 'start',
                })
            }
        }
    }, [])

    const clientFilterOption = (input, option) =>
        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0

    const isRemoteSearchLoading =
        remoteSearch &&
        (searchText !== debouncedSearch || options.isFetching)

    return (
        <Form.Item
            label={label}
            style={{ margin: 0 }}
            validateStatus={options.status}
        >
            <Select
                mode={multiple ? 'multiple' : ''}
                defaultOpen={autoFocus}
                ref={inputRef}
                showSearch={isSearch}
                loading={isRemoteSearchLoading || options.isLoading}
                notFoundContent={
                    isRemoteSearchLoading ? <Spin size="small" /> : null
                }
                defaultValue={defaultValue}
                value={value}
                onChange={(e) => onChange(e)}
                style={{ width: multiple ? 285 : 190 }}
                optionFilterProp={isSearch && !remoteSearch ? 'children' : null}
                filterOption={
                    isSearch && !remoteSearch ? clientFilterOption : false
                }
                filterSort={
                    isSearch && !remoteSearch
                        ? (optionA, optionB) =>
                              optionA.children
                                  .toLowerCase()
                                  .localeCompare(optionB.children.toLowerCase())
                        : null
                }
                onSearch={remoteSearch ? setSearchText : undefined}
            >
                {!noAll && (
                    <Option key={''} value={''}>
                        {t('OFFERS_ALL')}
                    </Option>
                )}
                {options.data?.list?.map((option) => {
                    const processedOption = optionProcessor
                        ? optionProcessor(option)
                        : option

                    return (
                        <Option
                            key={processedOption.id}
                            value={processedOption.id}
                        >
                            {t(processedOption.name)}
                        </Option>
                    )
                })}
            </Select>
        </Form.Item>
    )
}

export default LoadingSelect
